// Los botones "Continuar con Google / Apple / Facebook" de la entrada, de "Creá tu cuenta" y de "Tu cuenta" (decisión
// D51), cada uno solo si el servidor tiene su credencial (van a la API, que manda al proveedor y vuelve a
// /entrar/<proveedor>), y el ícono de con qué entró la cuenta.
import { cx, Icono } from '@reparto/ui';
import { type ReactNode, useState } from 'react';
import { mensajeDeError } from '../datos/api';
import { irAProveedor, NOMBRE_PROVEEDOR, type Proveedor } from '../datos/google';
import type { MetodoEntrada } from '../datos/tipos';
import { BotonApple, BotonFacebook, BotonGoogle, FotoGoogle, LogoApple, LogoFacebook } from './BotonGoogle';

const BOTON: Record<Proveedor, typeof BotonGoogle> = {
  google: BotonGoogle,
  apple: BotonApple,
  facebook: BotonFacebook,
};

export function EntrarCon({
  proveedores,
  volver = '/',
  alTocar,
  className,
}: {
  proveedores: Proveedor[];
  /** A qué pantalla ir después de entrar. */
  volver?: string;
  /** Otra cosa que ir a entrar (vincular el proveedor desde "Tu cuenta"). */
  alTocar?: (p: Proveedor) => Promise<void>;
  className?: string;
}) {
  const [yendo, setYendo] = useState<Proveedor | null>(null);
  const [error, setError] = useState<string | null>(null);
  if (!proveedores.length) return null;
  return (
    <div className={className}>
      <div className="flex flex-col gap-3">
        {proveedores.map((p) => {
          const Boton = BOTON[p];
          return (
            <Boton
              key={p}
              cargando={yendo === p}
              disabled={!!yendo && yendo !== p}
              onClick={async () => {
                setError(null);
                setYendo(p);
                try {
                  await (alTocar ? alTocar(p) : irAProveedor(p, volver));
                } catch (e) {
                  setYendo(null);
                  setError(
                    alTocar
                      ? mensajeDeError(e)
                      : `No se pudo abrir ${NOMBRE_PROVEEDOR[p]} en este navegador. Entrá con tu correo.`,
                  );
                }
              }}
            />
          );
        })}
      </div>
      {error ? (
        <p role="alert" className="mt-2 text-[15px] font-medium text-rose-700">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** La línea con "o" entre la entrada con correo y los proveedores. */
export function Separador({ children = 'o', className = 'my-6' }: { children?: ReactNode; className?: string }) {
  return (
    <div className={cx('flex items-center gap-3 text-[15px] text-stone-600', className)} role="separator">
      <span className="h-px flex-1 bg-stone-300" />
      {children}
      <span className="h-px flex-1 bg-stone-300" />
    </div>
  );
}

/** Con qué entró la cuenta (decisión D51): el texto y el ícono. */
export const entrasteCon = (m: MetodoEntrada | undefined): string =>
  m === 'google'
    ? 'Entraste con Google'
    : m === 'apple'
      ? 'Entraste con Apple'
      : m === 'facebook'
        ? 'Entraste con Facebook'
        : 'Tu cuenta';

export function IconoCuenta({ metodo, foto }: { metodo: MetodoEntrada | undefined; foto: string | null }) {
  if (metodo === 'google') return <FotoGoogle url={foto} />;
  return (
    <span
      className={cx(
        'grid h-10 w-10 shrink-0 place-items-center rounded-full',
        metodo === 'apple'
          ? 'bg-[#000000] text-[#ffffff]'
          : metodo === 'facebook'
            ? 'bg-[#0866ff]'
            : 'bg-primary-50 text-primary-700',
      )}
    >
      {metodo === 'apple' ? <LogoApple /> : metodo === 'facebook' ? <LogoFacebook /> : <Icono nombre="mail" />}
    </span>
  );
}
